import type { NextStep } from './next-step.js';

export interface TodayVisit {
  id: string;
  slot: string;
  propertyTitle: string;
  clientName: string;
  clientPhone: string | null;
}

export interface TodayData {
  visits: { requested: number; confirmed: number };
  deals: {
    needingAction: number;
    items: {
      id: string;
      state: string;
      propertyTitle: string;
      clientName: string;
      nextStep: NextStep | null;
      memoSigned: number;
      memoSigners: number;
    }[];
  };
  newRequests: TodayVisit[];
  todaysVisits: TodayVisit[];
  renewals: {
    dueSoon: number;
    items: {
      id: string;
      propertyTitle: string;
      dueOn: string;
      daysLeft: number;
      status: string;
      tenantPhone: string | null;
    }[];
  };
  enquiries: {
    due: number;
    items: {
      id: string;
      name: string;
      phone: string | null;
      propertyTitle: string | null;
      note: string | null;
      followUpAt: string;
    }[];
  };
  brokerage: {
    dueSoon: number;
    items: {
      id: string;
      propertyTitle: string;
      dueOn: string;
      daysLeft: number;
      balancePaise: number;
    }[];
  };
  unreadNotifications: number;
}

export interface AttentionSection {
  count: number;
  items: { id: string; propertyTitle: string; since: string; daysLate: number }[];
}

export interface Attention {
  waitingRequests: AttentionSection;
  brokerageDueSoon: AttentionSection;
  renewalsSoon: AttentionSection;
  overdueBrokerage: AttentionSection;
  unassignedVisits: AttentionSection;
  waitingMemos: AttentionSection;
  overdueRenewals: AttentionSection;
}

export interface Cell {
  branchId: string;
  branchName: string;
  memberId: string | null;
  memberName: string | null;
  visitsBooked: number;
  dealsStarted: number;
  dealsClosed: number;
  brokerageDuePaise: number;
  brokerageReceivedPaise: number;
  renewalsDue60: number;
  renewalsRecovered: number;
}

export interface Overview {
  days: number;
  activity: boolean;
  branches: Cell[];
  agents: Cell[];
}

export interface Totals {
  expectedPaise: number;
  receivedPaise: number;
  duePaise: number;
  overduePaise: number;
  incentiveOwedPaise: number;
  deals: number;
}

/** The sum of a column over branch cells, for the owner's hero. */
export function sumCells(cells: Cell[]): {
  received: number;
  due: number;
  closed: number;
  started: number;
  visits: number;
  renewalsDue: number;
  recovered: number;
} {
  return cells.reduce(
    (acc, c) => ({
      received: acc.received + c.brokerageReceivedPaise,
      due: acc.due + c.brokerageDuePaise,
      closed: acc.closed + c.dealsClosed,
      started: acc.started + c.dealsStarted,
      visits: acc.visits + c.visitsBooked,
      renewalsDue: acc.renewalsDue + c.renewalsDue60,
      recovered: acc.recovered + c.renewalsRecovered,
    }),
    { received: 0, due: 0, closed: 0, started: 0, visits: 0, renewalsDue: 0, recovered: 0 },
  );
}

/** Branches ranked by money received, then by name, so the order never jumps between equal rows. */
export function rankBranches(cells: Cell[]): Cell[] {
  return [...cells].sort(
    (a, b) =>
      b.brokerageReceivedPaise - a.brokerageReceivedPaise ||
      a.branchName.localeCompare(b.branchName),
  );
}

/** How many things need the person today, for the hero number. */
export function thingsToday(today: TodayData): {
  total: number;
  visits: number;
  deals: number;
  renewals: number;
  brokerage: number;
  followUps: number;
} {
  const visits = today.visits.requested + today.visits.confirmed;
  return {
    visits,
    deals: today.deals.needingAction,
    renewals: today.renewals.dueSoon,
    brokerage: today.brokerage.dueSoon,
    followUps: today.enquiries.due,
    total:
      visits +
      today.deals.needingAction +
      today.renewals.dueSoon +
      today.brokerage.dueSoon +
      today.enquiries.due,
  };
}
